import React, { useState, useEffect } from 'react';
import './index.scss';
import EmailOtpFlow from './EmailOtpFlow';
import MateAppFlow from '../../Components/Login/MateAppFlow';
import MainScreen from '../../Components/Login/MainScreen';
import { tLoader } from '@/Utils/i18n';
import { languageManager } from '@/Utils/languageManager';
import { GoogleOAuthProvider } from '@react-oauth/google';

const GOOGLE_CLIENT_ID = '701606934729-relrd44e3tt49j3ko5lc3ucamg4oojr8.apps.googleusercontent.com';

type LoginFlow = 'main' | 'email_otp' | 'mate_app';

const translations = {
    en: {
        "By continuing, you agree to our ": "By continuing, you agree to our ",
        "Terms of Use": "Terms of Use",
        "Privacy Policy": "Privacy Policy",
        " and ": " and "
    },
    id: {
        "By continuing, you agree to our ": "Dengan melanjutkan, Anda menyetujui ",
        "Terms of Use": "Ketentuan Penggunaan",
        "Privacy Policy": "Kebijakan Privasi",
        " and ": " dan "
    },
    ko: {
        "By continuing, you agree to our ": "계속 진행하면 다음에 동의하게 됩니다:\n",
        "Terms of Use": "이용약관",
        "Privacy Policy": "개인정보처리방침",
        " and ": " 및 "
    },
    vn: {
        "By continuing, you agree to our ": "Bằng cách tiếp tục, bạn đồng ý với ",
        "Terms of Use": "Điều khoản sử dụng",
        "Privacy Policy": "Chính sách bảo mật",
        " and ": " và "
    }
};

const LoginPage: React.FC = () => {
    const [currentFlow, setCurrentFlow] = useState<LoginFlow>('main');
    const [redirectTo, setRedirectTo] = useState(new URLSearchParams(window.location.search).get('redirectTo') ?? '/');
    const [urlParams, setUrlParams] = useState<{
        email?: string;
        userid?: string;
        otp?: string;
        autoVerify?: boolean;
    }>({});

    useEffect(() => {
        const queryParams = Object.fromEntries(new URLSearchParams(window.location.search).entries());
        
        let { otp, email, userid, redirectTo: redirect } = queryParams;
        
        // Store URL parameters
        const params = {
            email: email || '',
            userid: userid || '',
            otp: otp || '',
            autoVerify: !!(otp && (email || userid))
        };

        if (otp) {
            // If OTP is present, set redirect to personatest: 임시방편
            redirect = '/personatest/koreanstudytype';
        }
        setUrlParams(params);
        
        // Handle redirect URL
        if (redirect) {
            try {
                const url = new URL(redirect, window.location.origin);
                if (url.origin === window.location.origin || redirect.startsWith('/')) {
                    setRedirectTo(redirect);
                }
            } catch (e) {
                console.error('Invalid redirect URL', e);
            }
        }
        
        // Determine initial flow based on URL parameters
        if (otp) {
            setCurrentFlow('email_otp'); // Go to OTP step
        } else if (email || userid) {
            setCurrentFlow('email_otp'); // Go to email step
        }
    }, []);

    const handleLoginSuccess = () => {
        // Redirect after successful login
        window.location.href = redirectTo;
    };

    const handleBackToMain = () => {
        setCurrentFlow('main');
    };


    if (currentFlow === 'email_otp') {
        return (
            <EmailOtpFlow
                onSuccess={handleLoginSuccess}
                onBack={urlParams.email || urlParams.userid ? undefined : handleBackToMain}
                initialEmail={urlParams.email}
                initialUserid={urlParams.userid}
                initialOtp={urlParams.otp}
                autoVerify={urlParams.autoVerify}
                showBackButton={!(urlParams.email || urlParams.userid)}
            />
        );
    }

    if (currentFlow === 'mate_app') {
        return (
            <MateAppFlow
                onSuccess={handleLoginSuccess}
                onBack={handleBackToMain}
                showBackButton={true}
            />
        );
    }

    return (
        <MainScreen
            onMateSignin={() => setCurrentFlow('mate_app')}
            onEmailSignin={() => setCurrentFlow('email_otp')}
            onSuccess={handleLoginSuccess}
        />
    );
};

const LoginPageWrapper = () => {
    const [, forceUpdate] = useState({});
    const t = tLoader(translations);

    useEffect(() => {
        const unsubscribe = languageManager.subscribe(() => forceUpdate({}));
        return unsubscribe;
    }, []);

    return (
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
            <div className="login-page-wrapper">
                <div className="login-page">
                    <img src="/logo-white.svg" alt="Korean Mate Logo" className="logo" />
                    <div className="login-container">

                        <div className="login-form">
                            <LoginPage/>
                        </div>

                        <div className="login-footer">
                            <p>
                                {t("By continuing, you agree to our ")}
                                <a href="/termsofuse" target="_blank" rel="noopener noreferrer">
                                    {t("Terms of Use")}
                                </a>
                                {t(" and ")}
                                <a href="/privacypolicy" target="_blank" rel="noopener noreferrer">
                                    {t("Privacy Policy")}
                                </a>
                                {"."}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </GoogleOAuthProvider>
    );
};

export default LoginPageWrapper;